import { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";

/**
 * Drop-in cover image uploader. Stores the returned `storage_path` in the
 * parent's value (string). The preview proxies through /api/files/{path};
 * the protected endpoint authenticates via the session cookie which the
 * browser automatically sends on same-origin <img> requests.
 *
 * Props:
 *   value     - current storage_path string ("" when empty)
 *   onChange  - (newPath: string) => void
 *   yachtId   - optional yacht context for the upload endpoint
 *   folder    - upload folder (default "covers")
 *   testid    - data-testid base
 *   aspect    - tailwind aspect class (default aspect-[16/10])
 */
export default function ImageUpload({
  value,
  onChange,
  yachtId,
  folder = "covers",
  testid = "image-upload",
  aspect = "aspect-[16/10]",
  label = "cover",
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  const previewUrl = value ? `${API}/api/files/${value}` : "";

  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-uploading the same filename later
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Images must be under 10 MB.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", folder);
      if (yachtId) fd.append("yacht_id", yachtId);
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.storage_path);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const clear = () => onChange("");

  return (
    <div className="space-y-2" data-testid={testid}>
      <div
        className={`relative ${aspect} rounded-md overflow-hidden bg-zinc-100 border border-dashed border-zinc-300`}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="cover preview"
            className="h-full w-full object-cover"
            data-testid={`${testid}-preview`}
          />
        ) : (
          <button
            type="button"
            onClick={onPick}
            className="h-full w-full grid place-items-center text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50 transition-colors"
            data-testid={`${testid}-empty`}
          >
            <div className="flex flex-col items-center gap-1">
              <Upload className="h-6 w-6" />
              <div className="text-[11px]">Click to upload {label}</div>
            </div>
          </button>
        )}
        {busy && (
          <div className="absolute inset-0 grid place-items-center bg-white/70">
            <Loader2 className="h-6 w-6 animate-spin text-zinc-700" />
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onFile}
          data-testid={`${testid}-input`}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onPick}
          disabled={busy}
          data-testid={`${testid}-pick`}
        >
          <Upload className="h-3.5 w-3.5 mr-1.5" />
          {value ? "Replace photo" : "Upload photo"}
        </Button>
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clear}
            disabled={busy}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            data-testid={`${testid}-clear`}
          >
            <X className="h-3.5 w-3.5 mr-1" /> Remove
          </Button>
        )}
      </div>
    </div>
  );
}
